import { useCallback, useEffect, useMemo, useState } from "react";
import { useTeacherId } from "./useTeacherId";
import { useTeacherTasks } from "./useTeacherTasks";
import { usePracticeSessions } from "./usePracticeSessions";
import type { Task, TaskResult } from "../lib/taskEngine";
import { sessionTimestamp } from "../lib/dashboardGamification";

export type TaskFilterTab = "all" | "active" | "expired";

export interface TeacherRecentSubmission {
  taskId: string;
  taskTitle: string;
  taskType: Task["type"];
  studentName: string;
  bestScorePct: number;
  completedAt: number;
}

export interface TeacherTaskRow extends Task {
  isExpired: boolean;
  resultCount: number;
  avgScorePct: number;
  expiringSoon: boolean;
}

const TYPE_LABELS: Record<string, string> = {
  chord: "Acorde",
  scale: "Escala",
  mode: "Modo",
  melody: "Melodía",
  note: "Notas",
  finger: "Gimnasio",
  custom: "Personalizada",
};

const TYPE_COLORS: Record<string, string> = {
  chord: "#F59E0B",
  scale: "#FF4D4D",
  mode: "#06B6D4",
  melody: "#8B5CF6",
  note: "#22C55E",
  finger: "#EC4899",
  custom: "#8A8078",
};

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function resultCompletedAt(r: TaskResult): number {
  if (r.completedAt) return r.completedAt;
  const last = r.attempts[r.attempts.length - 1];
  return last?.timestamp ?? 0;
}

export function useTeacherDashboard() {
  const { teacherId, teacherName } = useTeacherId();
  const { tasks, results, isLoading, error, loadResults } = useTeacherTasks(teacherId);
  const { sessions: practiceSessions, loading: sessionsLoading } = usePracticeSessions();
  const [filterTab, setFilterTab] = useState<TaskFilterTab>("active");
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const now = Date.now();
  const activeTasks = useMemo(
    () => tasks.filter((t) => !t.expiresAt || now < t.expiresAt),
    [tasks, now]
  );
  const expiredTasks = useMemo(
    () => tasks.filter((t) => t.expiresAt && now >= t.expiresAt),
    [tasks, now]
  );

  useEffect(() => {
    activeTasks.slice(0, 8).forEach((t) => {
      if (!results[t.id]) void loadResults(t.id);
    });
  }, [activeTasks, results, loadResults]);

  const totalStudents = useMemo(() => {
    const set = new Set<string>();
    Object.values(results).forEach((arr) =>
      arr.forEach((r) => set.add(r.studentName || "Anónimo"))
    );
    return set.size;
  }, [results]);

  const { globalAverage, totalAttempts, scoreDistribution, totalSubmissions } = useMemo(() => {
    let totalScore = 0;
    let count = 0;
    let attempts = 0;
    const dist = { excellent: 0, good: 0, regular: 0, low: 0 };

    Object.values(results).forEach((arr) => {
      arr.forEach((r) => {
        totalScore += r.bestScore;
        count++;
        attempts += r.attempts.length;
        const pct = r.bestScore * 100;
        if (pct >= 90) dist.excellent++;
        else if (pct >= 70) dist.good++;
        else if (pct >= 50) dist.regular++;
        else dist.low++;
      });
    });

    return {
      globalAverage: count > 0 ? Math.round((totalScore / count) * 100) : 0,
      totalAttempts: attempts,
      totalSubmissions: count,
      scoreDistribution: dist,
    };
  }, [results]);

  const studentRanking = useMemo(() => {
    const map = new Map<string, { name: string; totalScore: number; count: number; tasks: number }>();
    Object.values(results).forEach((arr) => {
      arr.forEach((r) => {
        const key = r.studentName || "Anónimo";
        const prev = map.get(key);
        if (prev) {
          prev.totalScore += r.bestScore;
          prev.count++;
          prev.tasks++;
        } else {
          map.set(key, { name: key, totalScore: r.bestScore, count: 1, tasks: 1 });
        }
      });
    });
    return Array.from(map.values())
      .map((s) => ({ ...s, avgScore: Math.round((s.totalScore / s.count) * 100) }))
      .sort((a, b) => b.avgScore - a.avgScore)
      .slice(0, 8);
  }, [results]);

  const taskRows: TeacherTaskRow[] = useMemo(() => {
    return tasks.map((task) => {
      const taskResults = results[task.id] || [];
      const isExpired = Boolean(task.expiresAt && now >= task.expiresAt);
      const avg =
        taskResults.length > 0
          ? Math.round(
              (taskResults.reduce((s, r) => s + r.bestScore, 0) / taskResults.length) * 100
            )
          : 0;
      const expiringSoon =
        !isExpired &&
        Boolean(task.expiresAt && task.expiresAt - now > 0 && task.expiresAt - now <= SEVEN_DAYS_MS);
      return {
        ...task,
        isExpired,
        resultCount: taskResults.length,
        avgScorePct: avg,
        expiringSoon,
      };
    });
  }, [tasks, results, now]);

  const filteredTasks = useMemo(() => {
    if (filterTab === "active") return taskRows.filter((t) => !t.isExpired);
    if (filterTab === "expired") return taskRows.filter((t) => t.isExpired);
    return taskRows;
  }, [taskRows, filterTab]);

  const tasksWithoutAttempts = useMemo(
    () => activeTasks.filter((t) => !(results[t.id]?.length)),
    [activeTasks, results]
  );

  const expiringSoonTasks = useMemo(
    () => taskRows.filter((t) => t.expiringSoon),
    [taskRows]
  );

  const typeBreakdown = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks.forEach((t) => {
      counts[t.type] = (counts[t.type] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([type, count]) => ({
        type,
        label: TYPE_LABELS[type] || type,
        color: TYPE_COLORS[type] || "#8A8078",
        count,
      }))
      .sort((a, b) => b.count - a.count);
  }, [tasks]);

  const recentSubmissions = useMemo((): TeacherRecentSubmission[] => {
    const rows: TeacherRecentSubmission[] = [];
    tasks.forEach((task) => {
      (results[task.id] || []).forEach((r) => {
        rows.push({
          taskId: task.id,
          taskTitle: task.title,
          taskType: task.type,
          studentName: r.studentName || "Anónimo",
          bestScorePct: Math.round(r.bestScore * 100),
          completedAt: resultCompletedAt(r),
        });
      });
    });
    return rows.sort((a, b) => b.completedAt - a.completedAt).slice(0, 12);
  }, [tasks, results]);

  const songbookStats = useMemo(() => {
    if (practiceSessions.length === 0) return null;
    const totalSessions = practiceSessions.length;
    const totalDuration = practiceSessions.reduce((sum, s) => sum + s.duration, 0);
    const totalScore = practiceSessions.reduce((sum, s) => sum + s.score, 0);
    const totalAttempts = practiceSessions.reduce((sum, s) => sum + s.attempts, 0);
    const avgFlow = Math.round(
      practiceSessions.reduce((sum, s) => sum + s.flowScore, 0) / totalSessions
    );
    const songMap = new Map<
      string,
      { title: string; count: number; totalScore: number; totalAttempts: number }
    >();
    practiceSessions.forEach((s) => {
      const prev = songMap.get(s.songId);
      if (prev) {
        prev.count++;
        prev.totalScore += s.score;
        prev.totalAttempts += s.attempts;
      } else {
        songMap.set(s.songId, {
          title: s.songTitle,
          count: 1,
          totalScore: s.score,
          totalAttempts: s.attempts,
        });
      }
    });
    const topSongs = Array.from(songMap.entries())
      .map(([id, data]) => ({
        id,
        ...data,
        avgScore:
          data.totalAttempts > 0 ? Math.round((data.totalScore / data.totalAttempts) * 100) : 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    const lastSession = practiceSessions[0];
    const lastAt = lastSession ? sessionTimestamp(lastSession.createdAt) : 0;

    return {
      totalSessions,
      totalDuration,
      avgFlow,
      accuracyPct:
        totalAttempts > 0 ? Math.round((totalScore / totalAttempts) * 100) : 0,
      topSongs,
      lastTitle: lastSession?.songTitle ?? null,
      lastAt,
    };
  }, [practiceSessions]);

  const handleExpand = useCallback(
    async (taskId: string) => {
      if (expandedTask === taskId) {
        setExpandedTask(null);
        return;
      }
      setExpandedTask(taskId);
      if (!results[taskId]) await loadResults(taskId);
    },
    [expandedTask, results, loadResults]
  );

  const handleCopyCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch {
      /* ignore */
    }
  }, []);

  return {
    teacherId,
    teacherName,
    isLoading: isLoading || sessionsLoading,
    error,
    filterTab,
    setFilterTab,
    expandedTask,
    copiedCode,
    tasks,
    activeTasks,
    expiredTasks,
    filteredTasks,
    results,
    totalStudents,
    globalAverage,
    totalAttempts,
    totalSubmissions,
    scoreDistribution,
    studentRanking,
    tasksWithoutAttempts,
    expiringSoonTasks,
    typeBreakdown,
    recentSubmissions,
    songbookStats,
    handleExpand,
    handleCopyCode,
    loadResults,
    TYPE_LABELS,
    TYPE_COLORS,
  };
}
